import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './VerifyMembership.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const VerifyMembership = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [membership, setMembership] = useState(null);
  const [error, setError] = useState('');

  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [admitting, setAdmitting] = useState(false);
  const [admitResult, setAdmitResult] = useState(null);

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAuthenticated(true);
      fetchMembershipDetails(token);
      fetchEvents(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEvents = async (token) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/v1/admin/events`,
        {
          params: { pageSize: 500 },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const list = response.data?.data || response.data?.results || [];
      setEvents(Array.isArray(list) ? list : []);
    } catch (err) {
      // Silently — admit section will show empty picker.
      console.error('Failed to load events', err);
    }
  };

  const handleAdmit = async () => {
    if (!selectedEventId) {
      setAdmitResult({ success: false, reason: 'Please pick an event first.' });
      return;
    }
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setAdmitResult({ success: false, reason: 'Not authenticated.' });
      return;
    }
    try {
      setAdmitting(true);
      setAdmitResult(null);
      const response = await axios.post(
        `${API_BASE}/api/v1/admin/memberships/${id}/admit-to-event`,
        { eventId: parseInt(selectedEventId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdmitResult(response.data);
    } catch (err) {
      setAdmitResult({
        success: false,
        reason: err.response?.data?.message || 'Failed to admit',
      });
    } finally {
      setAdmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/api/v1/auth/login`,
        credentials
      );

      if (response.data.token) {
        const token = response.data.token;
        localStorage.setItem('adminToken', token);
        setAuthenticated(true);
        fetchMembershipDetails(token);
        fetchEvents(token);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipDetails = async (token) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/api/v1/memberships/verify/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setMembership(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load membership details');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        setAuthenticated(false);
      } else {
        setError(err.response?.data?.message || 'Failed to load membership details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthenticated(false);
    setMembership(null);
    navigate('/');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!authenticated) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="verify-card">
            <div className="verify-header">
              <h1>
                <i className="bi bi-shield-lock"></i> Membership Verification
              </h1>
              <p>Admin authentication required</p>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Logging in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Login
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !membership) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading membership details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !membership) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="verify-card">
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-membership-page">
      <div className="verify-container">
        <div className="verify-card">
          <div className="verify-header">
            <h1>
              <i className="bi bi-award"></i> Membership Details
            </h1>
            <button className="btn btn-sm btn-outline-light" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-2"></i>
              Logout
            </button>
          </div>

          {membership && (
            <div className="membership-details">
              <div className="status-section">
                <div className={`status-badge ${membership.isActive ? 'status-active' : 'status-inactive'}`}>
                  {membership.isActive ? (
                    <>
                      <i className="bi bi-check-circle-fill me-2"></i>
                      Active Membership
                    </>
                  ) : membership.isExpired ? (
                    <>
                      <i className="bi bi-x-circle-fill me-2"></i>
                      Expired
                    </>
                  ) : (
                    <>
                      <i className="bi bi-clock-fill me-2"></i>
                      {membership.status}
                    </>
                  )}
                </div>
              </div>

              <div className="details-grid">
                <div className="detail-item">
                  <div className="detail-label">Membership Code</div>
                  <div className="detail-value code-value">{membership.code || 'N/A'}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Type</div>
                  <div className="detail-value">{membership.type}</div>
                </div>

                <div className="detail-item full-width">
                  <div className="detail-label">Name</div>
                  <div className="detail-value">{membership.name}</div>
                </div>

                {membership.coupleName && (
                  <div className="detail-item full-width">
                    <div className="detail-label">Partner Name</div>
                    <div className="detail-value">{membership.coupleName}</div>
                  </div>
                )}

                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{membership.email}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Phone</div>
                  <div className="detail-value">{membership.phone}</div>
                </div>

                {membership.couplePhone && (
                  <div className="detail-item">
                    <div className="detail-label">Partner Phone</div>
                    <div className="detail-value">{membership.couplePhone}</div>
                  </div>
                )}

                {membership.residenceCountry && (
                  <div className="detail-item">
                    <div className="detail-label">Country</div>
                    <div className="detail-value">{membership.residenceCountry}</div>
                  </div>
                )}

                <div className="detail-item">
                  <div className="detail-label">Start Date</div>
                  <div className="detail-value">{formatDate(membership.startDate)}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">End Date</div>
                  <div className="detail-value">{formatDate(membership.endDate)}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Member Since</div>
                  <div className="detail-value">{formatDate(membership.createdAt)}</div>
                </div>
              </div>

              {membership.photo && (
                <div className="photo-section">
                  <div className="detail-label">Photo</div>
                  <img src={membership.photo} alt="Member" className="member-photo" />
                </div>
              )}

              <div className="admit-section" style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '1rem' }}>
                  <i className="bi bi-door-open me-2"></i>
                  Admit to Event
                </h3>
                {(membership.accessOrganizationId || (membership.accessRestaurantIds && membership.accessRestaurantIds.length > 0)) ? (
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    All memberships are valid at the restaurants configured in Membership Settings.
                  </p>
                ) : (
                  <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Membership access not configured. Ask an admin to set Organization &amp;
                    Restaurant Access in Membership Settings.
                  </div>
                )}
                <div className="form-group">
                  <label>Event</label>
                  <select
                    className="form-control"
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                  >
                    <option value="">— Select the event —</option>
                    {events.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name}{ev.eventDate ? ` · ${new Date(ev.eventDate).toLocaleDateString()}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleAdmit}
                  disabled={admitting || !selectedEventId}
                  style={{ marginTop: '1rem' }}
                >
                  {admitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Admitting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Admit
                    </>
                  )}
                </button>

                {admitResult && (
                  <div
                    className={`alert ${admitResult.success ? 'alert-success' : 'alert-danger'}`}
                    style={{ marginTop: '1rem' }}
                  >
                    {admitResult.success ? (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Admitted{' '}
                        {typeof admitResult.admittedCount === 'number' && (
                          <span>({admitResult.admittedCount}/{admitResult.cap} entries used)</span>
                        )}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-x-circle me-2"></i>
                        {admitResult.reason || 'Rejected'}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyMembership;
